/** The applicant's side: submitting the join-request form to the moderator. */

import { useMutation } from "@tanstack/react-query";
import { useNostr } from "@nostrify/react";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useToast } from "@/hooks/useToast";
import { MODERATOR_PUBKEY } from "@/lib/concord/config";
import { createJoinRequestEvent } from "@/lib/concord/requests";
import { publishToInbox } from "@/lib/concord/inbox";
import type { JoinRequest } from "@/lib/concord/types";

export function useSubmitApplication(
  onSubmitted: (request: JoinRequest) => void,
) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (request: JoinRequest) => {
      if (!user) throw new Error("You must be logged in");
      const event = await createJoinRequestEvent({
        signer: user.signer,
        applicantPub: user.pubkey,
        moderatorPub: MODERATOR_PUBKEY,
        request,
      });
      await publishToInbox(nostr, MODERATOR_PUBKEY, event);
      return request;
    },
    onSuccess: (request) => {
      onSubmitted(request);
      toast({
        title: "Application sent",
        description: "Your request to join is with the moderator now.",
      });
    },
    onError: (e) => {
      console.error(e);
      toast({
        title: "Couldn't send your application",
        description: "Something went wrong publishing your request. Try again.",
        variant: "destructive",
      });
    },
  });
}
